import type { CardListItem, CardStockUpdate, CardSummary } from "@hobbyshopify/backend-client";
import { Rx, RxList } from "subtree.js/core";
import type { CardListFilter } from "../../core/ops/cards-ops";
import { idleLoad, type LoadStatus } from "../../core/primitives/load-status";

export const CARD_FILTERS: { key: CardListFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "in-stock", label: "In stock" },
  { key: "out-of-stock", label: "Out of stock" },
  { key: "untracked", label: "Untracked" },
];

/** The card dialog that is open, and for which card. Price history is its own section. */
export interface CardDialog {
  readonly kind: "price" | "stock" | "archive";
  readonly card: CardSummary;
}

export interface CardPrices {
  readonly priceCents: number | null;
  readonly floorPriceCents: number | null;
}

export class CardsState {
  readonly filter = new Rx<CardListFilter>("all");
  readonly search = new Rx("");
  readonly status = new Rx<LoadStatus>(idleLoad);
  readonly items = new RxList<CardListItem>();
  /** The page after the last one shown, or null when the list is complete. */
  readonly nextCursor = new Rx<string | null>(null);
  /** The card a request is in flight for, if one is. */
  readonly busyCardId = new Rx<string | null>(null);
  readonly notice = new Rx<string | null>(null);
  readonly dialog = new Rx<CardDialog | null>(null);
  /** What the open dialog reports in place. */
  readonly dialogError = new Rx<string | null>(null);
  /** Whether the caller may reprice, archive or set price/floor/stock. `true` until the role is known. */
  readonly canEditInventory = new Rx(true);
  /** Counts tapped on a row and not yet sent, by card id. */
  readonly stockEdits = new Rx<Readonly<Record<string, number>>>({});
  /** Tracked stock at asking price; null while more pages exist. */
  readonly stockValueCents = new Rx<number | null>(null);
  /** Scans waiting in the review queue, for the Review tab. */
  readonly reviewCount = new Rx(0);
}

export abstract class CardsActions {
  abstract setFilter(filter: CardListFilter): void;
  abstract setSearch(search: string): void;
  abstract loadMore(): Promise<void>;
  abstract retry(): void;
  abstract reprice(card: CardSummary): Promise<void>;
  abstract openSetPrice(card: CardSummary): void;
  abstract openStock(card: CardSummary): void;
  /** One copy more or fewer; taps on one card add up into one change, sent once they stop. */
  abstract stepStock(card: CardSummary, step: number): void;
  abstract openArchive(card: CardSummary): void;
  abstract closeDialog(): void;
  abstract savePrices(next: CardPrices): Promise<void>;
  abstract saveStock(change: CardStockUpdate): Promise<void>;
  abstract confirmArchive(): Promise<void>;
  abstract dismissNotice(): void;
}
